import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { EnhancedPredictionOutput, AnomalyRecord, ForecastData } from '@/types'

interface PredictionState {
  // Data
  latestPredictions: Record<string, EnhancedPredictionOutput>
  anomalies: Record<string, AnomalyRecord[]>
  forecasts: Record<string, ForecastData>

  // Loading states
  loading: boolean
  error: string | null

  // Actions
  setPrediction: (assetId: string, prediction: EnhancedPredictionOutput) => void
  setAnomalies: (assetId: string, anomalies: AnomalyRecord[]) => void
  addAnomaly: (assetId: string, anomaly: AnomalyRecord) => void
  setForecast: (assetId: string, forecast: ForecastData) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Selectors
  getPrediction: (assetId: string) => EnhancedPredictionOutput | undefined
  getAnomalies: (assetId: string) => AnomalyRecord[]
  getForecast: (assetId: string) => ForecastData | undefined
  getTotalAnomalyCount: () => number
}

export const usePredictionStore = create<PredictionState>()(
  devtools(
    (set, get) => ({
      // Initial state
      latestPredictions: {},
      anomalies: {},
      forecasts: {},
      loading: false,
      error: null,

      // Actions
      setPrediction: (assetId, prediction) =>
        set((state) => ({
          latestPredictions: {
            ...state.latestPredictions,
            [assetId]: prediction
          }
        })),

      setAnomalies: (assetId, anomalies) =>
        set((state) => ({
          anomalies: {
            ...state.anomalies,
            [assetId]: anomalies
          }
        })),

      addAnomaly: (assetId, anomaly) =>
        set((state) => ({
          anomalies: {
            ...state.anomalies,
            [assetId]: [anomaly, ...(state.anomalies[assetId] || [])]
          }
        })),

      setForecast: (assetId, forecast) =>
        set((state) => ({
          forecasts: {
            ...state.forecasts,
            [assetId]: forecast
          }
        })),

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      // Selectors
      getPrediction: (assetId) => get().latestPredictions[assetId],

      getAnomalies: (assetId) => get().anomalies[assetId] || [],

      getForecast: (assetId) => get().forecasts[assetId],

      getTotalAnomalyCount: () =>
        Object.values(get().anomalies).reduce(
          (sum, list) => sum + list.length,
          0
        )
    }),
    { name: 'prediction-store' }
  )
)
